import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ApplicationLayout from 'v3/layouts/Application';
import { Card, Text, Button } from 'v3/components';
import InfiniteScroll from 'v3/components/InfiniteScroll';
import { cooperplaceApi } from 'services/api';
import Space from 'v3/components/Space';
import { useModal } from 'hooks';
import { useSnackbar } from 'v3/components/Snackbar';
import {
  LinkCard,
  EditLinkConfigModal,
  CloseLinkChecklistModal,
  AnsweredResponses,
} from '../ActiveLinkList';
import { AnsweredCard, AnsweredResponsesModal } from '../AnsweredList';
import { PendingCard, ResponseChecklistModal, CancelChecklistModal } from '../PendingList';
import RequestChecklist from '../RequestChecklist';
import PlateQuestionsModal from '../PlateQuestionsModal';
import ExternalLinkCreatedModal from '../ExternalLinkCreatedModal';
import { buildMissingResponsePlateItems, toResponsePlateAnswers } from '../plateQuestions';
import Filter from './Filter';
import { styles } from './styles';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';

const TABS = [
  { key: 'pending', label: 'Pendentes', Icon: AssignmentOutlinedIcon },
  { key: 'external', label: 'Links Externos', Icon: LinkOutlinedIcon },
  { key: 'answered', label: 'Respondidos', Icon: AssignmentTurnedInOutlinedIcon },
  { key: 'link', label: 'Checklists Públicos', Icon: LinkOutlinedIcon },
];

function isLinkTab(tabKey) {
  return tabKey === 'external' || tabKey === 'link';
}

function formatDateBR(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR');
}

function formatDateTimeBR(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR');
}

function toDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function diffDaysFrom(value) {
  const date = toDate(value);
  if (!date) return 0;
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  return Math.max(0, Math.floor(diff / 86400000));
}

function diffHoursFrom(value) {
  const date = toDate(value);
  if (!date) return 0;
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  return Math.max(0, Math.floor(diff / 3600000));
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function getCreatedAgoLabel(item) {
  const createdAt = toDate(item?.createdAt);
  if (!createdAt) {
    const daysFallback = Number(item?.daysSinceCreated) || 0;
    return `Criado há ${daysFallback} ${daysFallback === 1 ? 'dia' : 'dias'}`;
  }

  const days = diffDaysFrom(createdAt);
  if (days >= 1) {
    return `Criado há ${days} ${days === 1 ? 'dia' : 'dias'}`;
  }

  const hours = diffHoursFrom(createdAt);
  if (hours <= 0) return 'Criado há menos de 1 hora';
  return `Criado há ${hours} ${hours === 1 ? 'hora' : 'horas'}`;
}

function getValidUntilLabel(item) {
  const days =
    Number(item?.singleChecklistConfig?.expirationDays ?? item?.expirationDays ?? 0) || 0;
  if (!days) return null;
  const createdAt = toDate(item?.singleChecklistConfig?.createdAt || item?.createdAt);
  if (!createdAt) return null;
  return formatDateBR(addDays(createdAt, days));
}

function getLinkUrl(item) {
  if (item?.link?.url) return item.link.url;

  if (typeof item?.linkUrl === 'string') return item.linkUrl;
  if (typeof item?.publicUrl === 'string') return item.publicUrl;
  if (typeof item?.url === 'string') return item.url;

  if (item?.link?.token) {
    const base =
      import.meta.env.VITE_BASE_URL_PUBLIC_CHECKLIST ||
      import.meta.env.VITE_REDIRECR_URI ||
      'http://localhost:3000';
    return `${base}/checklist/public/${item.link.token}`;
  }

  return '';
}

function getLinkExpiresAt(item) {
  const days = Number(item?.link?.expirationDays ?? 0);
  if (!days) return null;

  const createdAt = toDate(item?.link?.createdAt || item?.link?.created_at);
  if (!createdAt) return null;

  const d = new Date(createdAt);
  d.setDate(d.getDate() + days);
  return d;
}

function buildLinkExpireLabel(item) {
  if (item?.linkIsClosed) return 'Encerrado';
  if (item?.link?.deletedAt) return 'Encerrado';
  if (item?.link?.isExpired) return 'Expirado';

  const expiresAt = getLinkExpiresAt(item);
  if (!expiresAt) return 'Sem expiração';

  return formatDateBR(expiresAt);
}

function countLinkResponses(item) {
  if (Array.isArray(item?.userResponses) && item.userResponses.length)
    return item.userResponses.length;

  if (item?.answeredAt) return 1;

  const groups = Array.isArray(item?.questionsResponses) ? item.questionsResponses : [];
  const answeredGroups = groups.filter((g) => !!g?.answeredAt).length;

  const slug = String(item?.checklistStatus?.slug || '').toUpperCase();
  const byStatus = slug && slug !== 'NOT_ANSWERED' ? 1 : 0;

  return Math.max(answeredGroups, byStatus);
}

function isUniqueResponseLink(item) {
  return !!item?.link?.responseUnique;
}

function hasLinkChecklist(item) {
  return !!(
    item?.linkUrl ||
    item?.link?.url ||
    item?.link?.token ||
    item?.link?.id ||
    item?.link?.createdAt
  );
}

function normalizeStatusFromBackend(item) {
  const slug = String(item?.checklistStatus?.slug || '').toUpperCase();
  if (slug === 'NOT_ANSWERED') return 'pending';
  if (slug === 'ANSWERED') return 'answered';
  if (slug === 'ANNULED') return 'annulled';
  return 'pending';
}

function getFirstAnsweredGroupInfo(item) {
  const groups = Array.isArray(item?.questionsResponses) ? item.questionsResponses : [];

  if (!groups.length) {
    return { answeredBy: '—', answeredAt: null };
  }

  const firstWithUser = groups.find((g) => {
    if (!g?.answeredAt) return false;

    const name = g?.user?.username || g?.user?.name;
    const n = String(name || '').trim();
    return n && n.toLowerCase() !== 'anônimo' && n.toLowerCase() !== 'anonimo';
  });

  if (firstWithUser) {
    const name = firstWithUser?.user?.username || firstWithUser?.user?.name;
    return { answeredBy: String(name).trim(), answeredAt: firstWithUser.answeredAt };
  }

  const firstAnswered = groups.find((g) => !!g?.answeredAt);
  if (firstAnswered) {
    return { answeredBy: 'Anônimo', answeredAt: firstAnswered.answeredAt };
  }

  return { answeredBy: '—', answeredAt: null };
}

function mapItemFromBackend(item) {
  const groups = (item?.checklistGroups || []).map((g) => g?.name).filter(Boolean);
  const isMaintenance = (item?.checklistGroups || []).some(
    (g) => String(g?.slug || '').toUpperCase() === 'MAINTENANCE',
  );

  const responsibleName =
    item?.originChecklist?.userCreated?.username || item?.originChecklist?.userCreated?.name || '—';

  const requestedByName = item?.createdUser?.username || 'Sistema';
  const first = getFirstAnsweredGroupInfo(item);
  const unique = isUniqueResponseLink(item);
  const responsesCount = countLinkResponses(item);
  const consumed = unique && responsesCount >= 1;
  const linkIsClosedComputed = !!item?.link?.deletedAt || consumed;
  const status = normalizeStatusFromBackend(item);
  const responseOrigin = hasLinkChecklist(item) ? 'external' : 'internal';
  const answeredAtValue = item?.answeredAt || first.answeredAt || item?.link?.deletedAt || null;
  const answeredAtDate = toDate(answeredAtValue);
  const answeredAtRaw = answeredAtDate ? answeredAtDate.getTime() : 0;
  const isCompletedResponse =
    responseOrigin === 'external' ? linkIsClosedComputed : status === 'answered';
  const isRecurring = item?.singleChecklistConfig?.isRecurring === true;
  const validUntilLabel = getValidUntilLabel(item);
  const createdAgoLabel = getCreatedAgoLabel(item);
  const originChecklistId = item?.checklistId ?? item?.originChecklist?.id ?? null;
  const originChecklistName = item?.originNameChecklist || item?.originChecklist?.name || '';
  const annulledByName = item?.updatedUser?.username || item?.updatedUser?.name || '';
  const annulledAtValue = item?.annulledAt || item?.updatedAt || item?.deletedAt || null;
  const annulledAtLabel = formatDateTimeBR(annulledAtValue);
  const plateDisplayLabel = buildPlateDisplayLabel(item);

  const mapped = {
    ...item,
    title: item?.name,
    description: item?.description,
    team: groups.length ? groups.join(', ') : '',
    responsible: responsibleName,
    requestedBy: requestedByName,
    requestedAt: formatDateBR(item?.createdAt),
    answeredBy: first.answeredBy,
    answeredAtLabel: formatDateBR(answeredAtValue),
    answeredAtRaw,
    status,
    responseOrigin,
    isCompletedResponse,
    isMaintenance,
    isRecurring,
    validUntilLabel,
    createdAgoLabel,
    originChecklistId,
    originChecklistName,
    annulledBy: annulledByName,
    annulledAtLabel,
    plateDisplayLabel,

    linkUrl: getLinkUrl(item),
    responsesCount,
    expireLabel: buildLinkExpireLabel({
      ...item,
      linkIsClosed: linkIsClosedComputed,
    }),

    linkIsAnonymous: !!item?.link?.isAnonymous,
    linkIsClosed: linkIsClosedComputed,
    linkIsExpired: !!item?.link?.isExpired,

    isUniqueLink: unique,
    isConsumedLink: consumed,
  };

  mapped.link = { ...(item?.link || {}) };
  if (linkIsClosedComputed && !mapped.link.deletedAt) {
    mapped.link.deletedAt = mapped.link.deletedAt || new Date().toISOString();
  }

  return mapped;
}

function buildParams(obj) {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([_, v]) => v !== undefined && v !== null && v !== '' && v !== false,
    ),
  );
}

function buildFilterParams(params, tabKey) {
  const isPending = tabKey === 'pending';
  const platesIds = Array.isArray(params.plates)
    ? params.plates.map((p) => (typeof p === 'object' && p !== null && 'id' in p ? p.id : p))
    : [];

  return {
    name: (params.q || '').trim() || undefined,
    groupsIds: params.groupIds?.length ? params.groupIds : undefined,
    plates: platesIds.length > 0 ? platesIds : undefined,
    searchResponse: !isPending ? (params.answersSearch || '').trim() || undefined : undefined,
    idSearch: params.idSearch,
  };
}

function toSearchText(value) {
  if (value === null || value === undefined) return '';
  return String(value).toLowerCase();
}

function toAlnumSearchText(value) {
  return toSearchText(value).replace(/[^a-z0-9]/g, '');
}

function getQuestionsFromItem(item) {
  const groups = Array.isArray(item?.questionsResponses) ? item.questionsResponses : [];
  return groups.flatMap((g) => (Array.isArray(g?.questions) ? g.questions : []));
}

function getQuestionSlug(question) {
  return String(question?.type?.slug || '').toLowerCase();
}

function normalizePlate(value) {
  const text = String(value || '')
    .trim()
    .toUpperCase();
  return text.replace(/\s+/g, '');
}

function pushUniquePlate(list, seen, value) {
  const plate = normalizePlate(value);
  if (!plate) return;
  const key = plate.replace(/[^A-Z0-9]/g, '');
  if (!key || seen.has(key)) return;
  seen.add(key);
  list.push(plate);
}

function extractChecklistPlateInfo(item) {
  const summaryVehiclePlates = Array.isArray(item?.plateSummary?.vehiclePlates)
    ? item.plateSummary.vehiclePlates
    : [];
  const summaryWagonPlates = Array.isArray(item?.plateSummary?.wagonPlates)
    ? item.plateSummary.wagonPlates
    : [];

  if (summaryVehiclePlates.length || summaryWagonPlates.length) {
    const vehiclePlates = [];
    const implementPlates = [];
    const vehicleSeen = new Set();
    const implementSeen = new Set();

    summaryVehiclePlates.forEach((plate) => pushUniquePlate(vehiclePlates, vehicleSeen, plate));
    summaryWagonPlates.forEach((plate) => pushUniquePlate(implementPlates, implementSeen, plate));

    return { isPlateChecklist: true, vehiclePlates, implementPlates };
  }

  const questions = getQuestionsFromItem(item);
  if (!questions.length) {
    return { isPlateChecklist: false, vehiclePlates: [], implementPlates: [] };
  }

  const vehiclePlates = [];
  const implementPlates = [];
  const vehicleSeen = new Set();
  const implementSeen = new Set();
  let isPlateChecklist = false;

  questions.forEach((question) => {
    const slug = getQuestionSlug(question);
    const fallbackPlate =
      question?.vehicle?.plate ||
      question?.wagon?.plate ||
      question?.plateOption?.plate ||
      question?.questionDescriptiveResponse ||
      '';

    if (slug === 'vehicle_plate') {
      isPlateChecklist = true;
      pushUniquePlate(vehiclePlates, vehicleSeen, question?.vehicle?.plate || fallbackPlate);
    }

    if (slug === 'wagon_plate' || slug === 'implement_plate') {
      isPlateChecklist = true;
      pushUniquePlate(implementPlates, implementSeen, question?.wagon?.plate || fallbackPlate);
    }
  });

  return { isPlateChecklist, vehiclePlates, implementPlates };
}

function buildPlateDisplayLabel(item) {
  const { isPlateChecklist, vehiclePlates, implementPlates } = extractChecklistPlateInfo(item);
  if (!isPlateChecklist) return '';

  if (vehiclePlates.length && implementPlates.length) {
    return `PLACA: ${vehiclePlates.join(', ')} | IMPLEMENTO: ${implementPlates.join(', ')}`;
  }

  if (vehiclePlates.length) {
    return `PLACA: ${vehiclePlates.join(', ')}`;
  }

  if (implementPlates.length) {
    return `IMPLEMENTO: ${implementPlates.join(', ')}`;
  }

  return '';
}

function questionHasSearchTerm(question, term, termAlnum) {
  if (!term) return true;

  const matchesValue = (value) => {
    if (!value) return false;
    const raw = toSearchText(value);
    if (raw.includes(term)) return true;
    if (!termAlnum) return false;
    return toAlnumSearchText(raw).includes(termAlnum);
  };

  if (matchesValue(question?.questionDescriptiveResponse)) return true;
  if (matchesValue(question?.logicQuestionResponse)) return true;

  const responses = Array.isArray(question?.responses) ? question.responses : [];
  for (const response of responses) {
    if (!response?.selected) continue;
    if (matchesValue(response?.description)) return true;
  }

  return false;
}

function itemHasAnswerSearch(item, value) {
  const term = toSearchText(value).trim();
  if (!term) return true;
  const termAlnum = toAlnumSearchText(term);

  const questions = getQuestionsFromItem(item);
  if (!questions.length) return false;

  return questions.some((q) => questionHasSearchTerm(q, term, termAlnum));
}

function hasActiveFilters(params) {
  const q = String(params?.q || '').trim();
  const groups = Array.isArray(params?.groupIds) ? params.groupIds : [];
  const plates = Array.isArray(params?.plates) ? params.plates : [];
  const answersSearch = String(params?.answersSearch || '').trim();
  const idSearch = String(params?.idSearch || '').trim();
  const linkStatusRaw = Array.isArray(params?.linkStatus)
    ? params.linkStatus
    : params?.linkStatus
      ? [params.linkStatus]
      : [];
  const linkStatus = linkStatusRaw
    .map(String)
    .map((v) => v.toLowerCase())
    .filter(Boolean);
  const linkStatusActive = linkStatus.length > 0 && !linkStatus.includes('all');
  const pendingStatus = String(params?.pendingStatus || 'active').toLowerCase();
  const pendingStatusActive = pendingStatus !== 'active' && pendingStatus !== 'all';
  return (
    !!q ||
    groups.length > 0 ||
    plates.length > 0 ||
    !!answersSearch ||
    linkStatusActive ||
    !!idSearch ||
    pendingStatusActive
  );
}

export default function CheckListSingleList() {
  const advancedFilters = useModal();
  const snackbar = useSnackbar();
  const [activeTab, setActiveTab] = useState('pending');
  const [filters, setFilters] = useState({
    q: '',
    groupIds: [],
    plates: [],
    answersSearch: '',
    linkStatus: ['all'],
    pendingStatus: 'active',
    idSearch: null,
  });
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, lastPage: 1, total: 0 });
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [selectedChecklist, setSelectedChecklist] = useState(null);
  const [isResponseOpen, setIsResponseOpen] = useState(false);
  const [badges, setBadges] = useState({ pending: 0, external: 0, answered: 0, link: 0 });
  const [isEditLinkOpen, setIsEditLinkOpen] = useState(false);
  const [editingLinkItem, setEditingLinkItem] = useState(null);
  const [isCloseLinkOpen, setIsCloseLinkOpen] = useState(false);
  const [closingLinkItem, setClosingLinkItem] = useState(null);
  const [closingLinkLoading, setClosingLinkLoading] = useState(false);
  const [isCancelPendingOpen, setIsCancelPendingOpen] = useState(false);
  const [cancelPendingItem, setCancelPendingItem] = useState(null);
  const [cancelPendingLoading, setCancelPendingLoading] = useState(false);
  const [cancelPendingError, setCancelPendingError] = useState('');
  const [isLinkAnswersOpen, setIsLinkAnswersOpen] = useState(false);
  const [linkAnswersItem, setLinkAnswersItem] = useState(null);
  const [isAnswersOpen, setIsAnswersOpen] = useState(false);
  const [answersItem, setAnswersItem] = useState(null);
  const [viewAnswersLoadingId, setViewAnswersLoadingId] = useState(null);
  const [advancedFiltersQtd, setAdvancedFiltersQtd] = useState(0);
  const [externalLinkItem, setExternalLinkItem] = useState(null);
  const [externalLinkPlateItems, setExternalLinkPlateItems] = useState([]);
  const [externalLinkPlateModalOpen, setExternalLinkPlateModalOpen] = useState(false);
  const [externalLinkLoadingId, setExternalLinkLoadingId] = useState(null);
  const [externalLinkCreating, setExternalLinkCreating] = useState(false);
  const [externalLinkError, setExternalLinkError] = useState('');
  const [generatedExternalLink, setGeneratedExternalLink] = useState(null);
  const requestIdRef = useRef(0);

  function extractTotal(respData) {
    const total =
      respData?.meta?.total ??
      respData?.meta?.pagination?.total ??
      respData?.total ??
      respData?.count;

    if (typeof total === 'number') return total;
    if (typeof total === 'string' && total.trim() !== '') return Number(total) || 0;

    const arr = respData?.data || [];
    return Array.isArray(arr) ? arr.length : 0;
  }

  function extractPagination(respData) {
    const meta = respData?.meta || {};
    const paginationMeta = meta.pagination || meta;
    const current =
      paginationMeta.current_page ?? paginationMeta.currentPage ?? paginationMeta.page ?? 1;
    const last = paginationMeta.last_page ?? paginationMeta.lastPage ?? paginationMeta.pages ?? 1;
    return {
      page: Number(current) || 1,
      lastPage: Number(last) || 1,
      total: extractTotal(respData),
    };
  }

  async function fetchTotalForTab(params, tabKey) {
    const pendingStatus = String(params?.pendingStatus || 'active').toLowerCase();
    const pendingStatusParam =
      tabKey === 'pending' && pendingStatus !== 'all'
        ? pendingStatus === 'annulled'
          ? 'ANNULED'
          : 'NOT_ANSWERED'
        : undefined;
    const tabFlags =
      tabKey === 'pending'
        ? pendingStatusParam === 'ANNULED'
          ? {}
          : { isPending: true }
        : tabKey === 'answered'
          ? { isResponsed: true }
          : tabKey === 'external'
            ? { isActiveLink: true, linkOrigin: 'EXTERNAL' }
            : tabKey === 'link'
              ? { isActiveLink: true, linkOrigin: 'USEFUL' }
              : {};

    const requestParams = buildParams({
      ...buildFilterParams(params, tabKey),
      page: 1,
      perPage: 1,
      status: pendingStatusParam,
      ...tabFlags,
    });

    const { data } = await cooperplaceApi.get('/single-checklist', { params: requestParams });
    return extractTotal(data);
  }

  const fetchBadgesWithFilters = useCallback(async (params, activeKey) => {
    try {
      const tabs = ['pending', 'external', 'answered', 'link'].filter((tab) => tab !== activeKey);
      const results = await Promise.all(
        tabs.map(async (tab) => ({
          tab,
          total: await fetchTotalForTab(params, tab),
        })),
      );

      setBadges((prev) => {
        const next = { ...prev };
        results.forEach(({ tab, total }) => {
          next[tab] = total;
        });
        return next;
      });
    } catch (e) { }
  }, []);

  const fetchBadges = useCallback(
    async (pendingStatusOverride) => {
      try {
        const pendingStatus = String(
          pendingStatusOverride ?? filters?.pendingStatus ?? 'active',
        ).toLowerCase();
        const pendingStatusParam =
          pendingStatus === 'annulled'
            ? 'ANNULED'
            : pendingStatus === 'all'
              ? undefined
              : 'NOT_ANSWERED';

        const [baseResp, pendingResp] = await Promise.all([
          cooperplaceApi.get('/single-checklist/count'),
          pendingStatusParam
            ? cooperplaceApi.get('/single-checklist/count', {
              params: { status: pendingStatusParam },
            })
            : null,
        ]);

        const baseData = baseResp?.data || {};
        const pendingData = pendingResp?.data || {};
        const pendingValue = pendingStatusParam
          ? Number(pendingData?.pendingResponses) || 0
          : Number(baseData?.pendingResponses) || 0;

        setBadges({
          pending: pendingValue,
          external: Number(baseData?.externalLinks) || 0,
          answered: Number(baseData?.answeredResponses) || 0,
          link: Number(baseData?.usefulLinks) || 0,
        });
      } catch (e) { }
    },
    [filters?.pendingStatus],
  );

  const fetchData = useCallback(
    async (params = filters, tabKey = activeTab, page = 1) => {
      const myReqId = ++requestIdRef.current;
      const hasAnswerFilters = String(params?.answersSearch || '').trim() !== '';

      if (tabKey === 'pending' && hasAnswerFilters) {
        if (page === 1) {
          setList([]);
          setPagination({ page: 1, lastPage: 1, total: 0 });
        }
        setLoading(false);
        setIsLoadingMore(false);
        return;
      }

      if (page === 1) {
        setList([]);
        setPagination({ page: 1, lastPage: 1, total: 0 });
        setIsLoadingMore(false);
        setLoading(true);
      } else {
        setIsLoadingMore(true);
        setLoading(false);
      }

      try {
        const pendingStatus = String(params?.pendingStatus || 'active').toLowerCase();
        const pendingStatusParam =
          tabKey === 'pending' && pendingStatus !== 'all'
            ? pendingStatus === 'annulled'
              ? 'ANNULED'
              : 'NOT_ANSWERED'
            : undefined;
        const tabFlags =
          tabKey === 'pending'
            ? pendingStatusParam === 'ANNULED'
              ? {}
              : { isPending: true }
            : tabKey === 'answered'
              ? { isResponsed: true }
              : tabKey === 'external'
                ? { isActiveLink: true, linkOrigin: 'EXTERNAL' }
                : tabKey === 'link'
                  ? { isActiveLink: true, linkOrigin: 'USEFUL' }
                  : {};

        const requestParams = buildParams({
          ...buildFilterParams(params, tabKey),
          page,
          perPage: 10,
          status: pendingStatusParam,
          ...tabFlags,
        });

        const { data } = await cooperplaceApi.get('/single-checklist', { params: requestParams });

        if (myReqId !== requestIdRef.current) return;

        const arr = data?.data || [];
        const nextPagination = extractPagination(data);
        setPagination(nextPagination);
        const normalized = (Array.isArray(arr) ? arr : []).map(mapItemFromBackend);
        const allowAdvancedSearch = tabKey !== 'pending';
        const answersSearch = allowAdvancedSearch ? (params?.answersSearch || '').trim() : '';

        const advancedFiltered = normalized.filter((item) => {
          if (answersSearch && !itemHasAnswerSearch(item, answersSearch)) return false;

          return true;
        });

        const rawStatusValues = Array.isArray(params?.linkStatus)
          ? params.linkStatus
          : params?.linkStatus
            ? [params.linkStatus]
            : [];
        const normalizedStatusValues = rawStatusValues.map(String).map((v) => v.toLowerCase());
        const useAllStatus =
          normalizedStatusValues.includes('all') || normalizedStatusValues.length === 0;

        const filteredByLinkStatus =
          !isLinkTab(tabKey) || useAllStatus
            ? advancedFiltered
            : advancedFiltered.filter((item) => {
              const isClosed = !!item?.linkIsClosed;
              const isExpired = !!item?.linkIsExpired;
              const isActive = !isClosed && !isExpired;
              return (
                (normalizedStatusValues.includes('active') && isActive) ||
                (normalizedStatusValues.includes('expired') && isExpired && !isClosed) ||
                (normalizedStatusValues.includes('closed') && isClosed)
              );
            });

        const pendingBase = advancedFiltered.filter(
          (it) => (it.status === 'pending' || it.status === 'annulled') && !hasLinkChecklist(it),
        );
        const filteredByPendingStatus =
          tabKey !== 'pending'
            ? pendingBase
            : pendingStatus === 'all'
              ? pendingBase
              : pendingStatus === 'annulled'
                ? pendingBase.filter((it) => it.status === 'annulled')
                : pendingStatus === 'expired'
                  ? pendingBase.filter((it) => it.status === 'pending' && !!it.isExpired)
                  : pendingBase.filter((it) => it.status === 'pending' && !it.isExpired);

        const finalList = isLinkTab(tabKey)
          ? [...filteredByLinkStatus].sort((a, b) => {
            const aClosed = !!a?.linkIsClosed;
            const bClosed = !!b?.linkIsClosed;
            if (aClosed !== bClosed) return aClosed ? 1 : -1;

            const aExpired = !!a?.linkIsExpired;
            const bExpired = !!b?.linkIsExpired;
            if (aExpired !== bExpired) return aExpired ? 1 : -1;

            const aTime = new Date(a?.link?.createdAt || a?.createdAt || 0).getTime();
            const bTime = new Date(b?.link?.createdAt || b?.createdAt || 0).getTime();
            return bTime - aTime;
          })
          : (() => {
            if (tabKey === 'answered') {
              const filtered = advancedFiltered.filter((it) => it.isCompletedResponse);
              return [...filtered].sort(
                (a, b) => (b.answeredAtRaw || 0) - (a.answeredAtRaw || 0),
              );
            }
            if (tabKey === 'pending') {
              return filteredByPendingStatus;
            }
            return advancedFiltered.filter((it) => it.status === tabKey);
          })();

        let nextList = [];
        setList((prev) => {
          nextList = page === 1 ? finalList : [...prev, ...finalList];
          return nextList;
        });

        if (hasActiveFilters(params)) {
          if (tabKey === 'pending') {
            setBadges((prev) => ({ ...prev, pending: nextList.length }));
          } else {
            setBadges((prev) => ({ ...prev, [tabKey]: nextPagination.total }));
          }
        }

        if (isLinkTab(tabKey) && !useAllStatus) {
          setBadges((prev) => ({ ...prev, [tabKey]: nextList.length }));
        }

        if (tabKey === 'pending' && pendingStatus !== 'active' && pendingStatus !== 'all') {
          setBadges((prev) => ({ ...prev, pending: nextList.length }));
        }
      } catch (e) {
        if (myReqId === requestIdRef.current && page === 1) {
          setList([]);
          setPagination({ page: 1, lastPage: 1, total: 0 });
        }
      } finally {
        if (myReqId === requestIdRef.current) {
          setLoading(false);
          setIsLoadingMore(false);
        }
      }
    },
    [activeTab, filters],
  );

  useEffect(() => {
    fetchData(filters, activeTab, 1);
  }, [activeTab]);

  useEffect(() => {
    fetchBadges();
  }, []);

  function openResponseQuestions(item, mode = 'answer') {
    setSelectedChecklist({ ...item, __mode: mode });
    setIsResponseOpen(true);
  }

  function openCancelPending(item) {
    setCancelPendingItem(item);
    setIsCancelPendingOpen(true);
    setCancelPendingError('');
  }

  function getApiError(error, fallback) {
    return String(error?.response?.data?.error || error?.response?.data?.message || fallback);
  }

  async function createPendingExternalLink(item, plateAnswers = []) {
    if (!item?.id) return;

    try {
      setExternalLinkCreating(true);
      setExternalLinkLoadingId(item.id);
      setExternalLinkError('');

      const { data } = await cooperplaceApi.post(`/single-checklist/${item.id}/link`, {
        ...(plateAnswers.length ? { plateAnswers } : null),
      });
      const created = data?.data || data || {};

      if (!created?.link) {
        throw new Error('A API não retornou o link criado.');
      }

      setExternalLinkPlateModalOpen(false);
      setGeneratedExternalLink(created.link);
    } catch (error) {
      const message = getApiError(error, 'Não foi possível gerar o link externo.');
      setExternalLinkError(message);

      if (!externalLinkPlateModalOpen) {
        snackbar.show(<Text>{message}</Text>, { type: 'error' });
      }
    } finally {
      setExternalLinkCreating(false);
      setExternalLinkLoadingId(null);
    }
  }

  async function openExternalLink(item) {
    if (!item?.id || externalLinkLoadingId) return;

    try {
      setExternalLinkItem(item);
      setExternalLinkError('');
      setExternalLinkLoadingId(item.id);

      const { data } = await cooperplaceApi.get(`/single-checklist/${item.id}/response`);
      const missingPlateItems = buildMissingResponsePlateItems(data?.questionsResponses || []);

      if (missingPlateItems.length) {
        setExternalLinkPlateItems(missingPlateItems);
        setExternalLinkPlateModalOpen(true);
        return;
      }

      await createPendingExternalLink(item);
    } catch (error) {
      const message = getApiError(error, 'Não foi possível carregar as perguntas do checklist.');
      snackbar.show(<Text>{message}</Text>, { type: 'error' });
      setExternalLinkItem(null);
    } finally {
      setExternalLinkLoadingId(null);
    }
  }

  async function confirmExternalLinkPlates(selections) {
    if (!externalLinkItem) return;
    await createPendingExternalLink(externalLinkItem, toResponsePlateAnswers(selections));
  }

  function closeGeneratedExternalLink() {
    setGeneratedExternalLink(null);
    setExternalLinkItem(null);
    setExternalLinkPlateItems([]);
    setExternalLinkError('');
    setActiveTab('external');
    fetchBadges();
  }

  function openAnswersModal(item, origin = 'answered') {
    if (origin === 'link') {
      setLinkAnswersItem(item);
      setIsLinkAnswersOpen(true);
      return;
    }
    setAnswersItem(item);
    setIsAnswersOpen(true);
  }

  async function handleViewAnswers(item, origin) {
    if (origin !== 'link') {
      setAnswersItem(item);
      setIsAnswersOpen(true);
      return;
    }

    if (!item?.id) return;

    try {
      setViewAnswersLoadingId(item.id);

      const { data } = await cooperplaceApi.get(`/single-checklist/${item.id}`);
      const updated = mapItemFromBackend(data);

      setList((prev) => prev.map((it) => (it.id === updated.id ? updated : it)));
      setLinkAnswersItem(updated);
      setIsLinkAnswersOpen(true);
    } catch (e) {
      setLinkAnswersItem(item);
      setIsLinkAnswersOpen(true);
    } finally {
      setViewAnswersLoadingId(null);
    }
  }

  async function handleConfirmCancelPending(reason) {
    if (!cancelPendingItem?.id) return;

    try {
      setCancelPendingLoading(true);
      setCancelPendingError('');

      await cooperplaceApi.put(`/single-checklist/${cancelPendingItem.id}`, {
        isAnnulled: true,
        deleteDescription: reason,
      });

      setIsCancelPendingOpen(false);
      setCancelPendingItem(null);
      await fetchBadges();
      fetchData(filters, activeTab, 1);
    } catch (e) {
      const backMsg = e?.response?.data?.error || e?.response?.data?.message || '';
      setCancelPendingError(backMsg ? String(backMsg) : 'Nao foi possivel cancelar o checklist.');
    } finally {
      setCancelPendingLoading(false);
    }
  }

  async function handleCopyLink(link) {
    try {
      await navigator.clipboard.writeText(link || '');
    } catch (e) { }
  }

  return (
    <ApplicationLayout
      title="Checklist Avulso"
      RightComponent={
        <Space x="0.5rem">
          <Button variant="secondary" onClick={() => advancedFilters.open()}>
            <Text weight={500} color="dark" type="regular">
              {advancedFiltersQtd ? `Filtro avançado (${advancedFiltersQtd})` : 'Filtro avançado'}
            </Text>
          </Button>
          <Button onClick={() => setIsRequestModalOpen(true)} disabled={loading}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 18, fontWeight: 700, lineHeight: '18px' }}>+</span>
              <Text type="regular" weight={600}>
                Solicitar Checklist
              </Text>
            </div>
          </Button>
        </Space>
      }
    >
      <Filter
        loading={loading}
        showAdvancedFilter={advancedFilters.isOpen}
        onCloseAdvanceFilterModal={() => advancedFilters.close()}
        setParentFilter={setFilters}
        setAdvancedFiltersQtd={setAdvancedFiltersQtd}
        activeTab={activeTab}
        onFilterChange={(next) => {
          setFilters(next);
          fetchData(next, activeTab, 1);

          const pendingStatus = String(next?.pendingStatus || 'active').toLowerCase();
          const skipOtherTabBadges = activeTab === 'pending' && pendingStatus !== 'all';

          if (!skipOtherTabBadges && hasActiveFilters(next)) {
            fetchBadgesWithFilters(next, activeTab);
          } else if (!skipOtherTabBadges) {
            fetchBadges(pendingStatus);
          } else if (pendingStatus !== 'all') {
            fetchBadges(pendingStatus);
          }
        }}
      />

      <Card>
        <div style={styles.tabsWrap}>
          {TABS.map((tab) => {
            const isActive = activeTab === tab.key;
            const Icon = tab.Icon;
            const badge = badges[tab.key] || 0;
            const hasAnswerFilters = String(filters.answersSearch || '').trim() !== '';
            const displayBadge = tab.key === 'pending' && hasAnswerFilters ? 0 : badge;

            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                style={styles.tabBtn(isActive)}
                disabled={loading}
              >
                <Icon style={{ fontSize: 18, color: isActive ? '#111827' : '#6B7280' }} />
                <span style={styles.tabLabel(isActive)}>{tab.label}</span>
                <span style={styles.tabBadge(isActive)}>{displayBadge}</span>
              </button>
            );
          })}
        </div>

        <InfiniteScroll
          loading={loading && !isLoadingMore}
          data={list}
          hasMore={pagination.page < pagination.lastPage}
          scrollThreshold={0.7}
          onEndReach={() => {
            if (loading || isLoadingMore) return;
            if (pagination.page >= pagination.lastPage) return;
            fetchData(filters, activeTab, pagination.page + 1);
          }}
          loader={
            isLoadingMore ? (
              <div
                style={{
                  width: '100%',
                  padding: '12px 0',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Text type="body" color="dark">
                  Carregando...
                </Text>
              </div>
            ) : null
          }
          renderItem={(item) => (
            <div key={item.id} style={{ width: '100%' }}>
              {isLinkTab(activeTab) ? (
                <LinkCard
                  item={item}
                  styles={styles}
                  onCopyLink={handleCopyLink}
                  onEditConfig={() => {
                    setEditingLinkItem(item);
                    setIsEditLinkOpen(true);
                  }}
                  onCloseChecklist={() => {
                    setClosingLinkItem(item);
                    setIsCloseLinkOpen(true);
                  }}
                  onViewAnswers={() => handleViewAnswers(item, 'link')}
                  viewAnswersLoading={viewAnswersLoadingId === item.id}
                  showViewAnswers={activeTab !== 'external'}
                />
              ) : activeTab === 'answered' ? (
                <AnsweredCard
                  item={item}
                  styles={styles}
                  onViewAnswers={() => openAnswersModal(item)}
                />
              ) : (
                <PendingCard
                  item={item}
                  styles={styles}
                  onAnswer={() => openResponseQuestions(item)}
                  onCancel={() => openCancelPending(item)}
                  onExternalLink={() => openExternalLink(item)}
                  externalLinkLoading={externalLinkLoadingId === item.id}
                />
              )}
            </div>
          )}
        />

        {!loading && list.length === 0 && (
          <div style={{ padding: '12px 0', textAlign: 'center' }} />
        )}
      </Card>

      {isRequestModalOpen && (
        <RequestChecklist
          open={isRequestModalOpen}
          onClose={() => setIsRequestModalOpen(false)}
          onCreated={(created) => {
            setIsRequestModalOpen(false);
            const nextTab = getLinkUrl(created) ? 'link' : 'pending';
            setActiveTab(nextTab);
            fetchData(filters, nextTab, 1);
            fetchBadges();
          }}
        />
      )}

      <ResponseChecklistModal
        open={isResponseOpen}
        onClose={() => setIsResponseOpen(false)}
        checklistTitle={selectedChecklist?.title}
        id={selectedChecklist?.id}
        singleChecklistId={selectedChecklist?.id}
        mode={selectedChecklist?.__mode}
        onSuccess={() => {
          const nextTab = 'answered';
          setActiveTab(nextTab);
          fetchData(filters, nextTab, 1);
          fetchBadges();
        }}
      />

      <CancelChecklistModal
        open={isCancelPendingOpen}
        checklistId={cancelPendingItem?.id || null}
        checklistTitle={cancelPendingItem?.title || ''}
        loading={cancelPendingLoading}
        onClose={() => {
          if (cancelPendingLoading) return;
          setIsCancelPendingOpen(false);
          setCancelPendingItem(null);
          setCancelPendingError('');
        }}
        onConfirm={handleConfirmCancelPending}
        errorMessage={cancelPendingError}
      />

      <PlateQuestionsModal
        open={externalLinkPlateModalOpen}
        items={externalLinkPlateItems}
        loading={externalLinkCreating}
        error={externalLinkError}
        onClose={() => {
          if (externalLinkCreating) return;
          setExternalLinkPlateModalOpen(false);
          setExternalLinkItem(null);
          setExternalLinkPlateItems([]);
          setExternalLinkError('');
        }}
        onConfirm={confirmExternalLinkPlates}
      />

      <ExternalLinkCreatedModal
        open={!!generatedExternalLink}
        link={generatedExternalLink}
        onClose={closeGeneratedExternalLink}
      />

      <EditLinkConfigModal
        open={isEditLinkOpen}
        onClose={() => {
          setIsEditLinkOpen(false);
          setEditingLinkItem(null);
        }}
        onSave={async ({ anonymous, days, closed }) => {
          if (!editingLinkItem?.id) return;

          const payload = {
            expirationDays: Number(days) || 0,
            isAnonymous: !!anonymous,
            isClosed: !!closed,
          };

          await cooperplaceApi.put(`/single-checklist/${editingLinkItem.id}`, payload);
          const { data } = await cooperplaceApi.get(`/single-checklist/${editingLinkItem.id}`);
          const updated = mapItemFromBackend(data);

          if (updated.isCompletedResponse) {
            await fetchData(filters, isLinkTab(activeTab) ? activeTab : 'link', 1);
          } else {
            setList((prev) => prev.map((it) => (it.id === updated.id ? updated : it)));
          }
          await fetchBadges();
        }}
        checklistTitle={editingLinkItem?.title || ''}
        responsesCount={editingLinkItem?.responsesCount ?? countLinkResponses(editingLinkItem)}
        initialAnonymous={!!editingLinkItem?.link?.isAnonymous}
        initialDays={Number(editingLinkItem?.link?.expirationDays ?? 0) || 0}
        initialClosed={!!editingLinkItem?.linkIsClosed}
        isLinkClosed={!!editingLinkItem?.linkIsClosed}
      />

      <CloseLinkChecklistModal
        open={isCloseLinkOpen}
        loading={closingLinkLoading}
        loadingLabel="Encerrando..."
        isLinkClosed={!!closingLinkItem?.linkIsClosed}
        onClose={() => {
          if (closingLinkLoading) return;
          setIsCloseLinkOpen(false);
          setClosingLinkItem(null);
        }}
        checklistTitle={closingLinkItem?.title || ''}
        checklistId={closingLinkItem?.id || null}
        responsesCount={closingLinkItem?.responsesCount ?? countLinkResponses(closingLinkItem)}
        onConfirm={async () => {
          if (!closingLinkItem?.id || closingLinkLoading || closingLinkItem?.linkIsClosed) return;

          try {
            setClosingLinkLoading(true);

            await cooperplaceApi.put(`/single-checklist/${closingLinkItem.id}`, { isClosed: true });

            const { data } = await cooperplaceApi.get(`/single-checklist/${closingLinkItem.id}`);
            const updated = mapItemFromBackend(data);

            if (updated.isCompletedResponse) {
              await fetchData(filters, isLinkTab(activeTab) ? activeTab : 'link', 1);
            }
            await fetchBadges();

            setIsCloseLinkOpen(false);
            setClosingLinkItem(null);
          } catch (e) {
          } finally {
            setClosingLinkLoading(false);
          }
        }}
      />

      <AnsweredResponses
        open={isLinkAnswersOpen}
        onClose={() => {
          setIsLinkAnswersOpen(false);
          setLinkAnswersItem(null);
        }}
        singleChecklistId={linkAnswersItem?.id}
        checklistTitle={linkAnswersItem?.title || ''}
      />

      <AnsweredResponsesModal
        open={isAnswersOpen}
        onClose={() => {
          setIsAnswersOpen(false);
          setAnswersItem(null);
        }}
        singleChecklistId={answersItem?.id}
        checklistTitle={answersItem?.title || ''}
      />
    </ApplicationLayout>
  );
}
